/**
 * Overview → Download Excel: same GST e-invoice document as Download JSON,
 * flattened into a workbook.
 *
 * Sheets:
 *   Summary    — Version + TranDtls / DocDtls / SellerDtls / BuyerDtls / ValDtls
 *   Line Items — ItemList rows from buildEInvoiceDocument
 *
 * Not a Candidate/Days/Rate/Amount billing sheet (that format is for
 * contractual upload only — see the helpers at the bottom of this file).
 */
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { buildEInvoiceDocument, collectLineItems } from '@/utils/einvoice-json-builder'
import { buildEInvoiceDownloadFilename } from '@/utils/invoice-download-filename'

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

export const EINVOICE_SUMMARY_SHEET = 'Summary'
export const EINVOICE_LINE_ITEMS_SHEET = 'Line Items'
export const EINVOICE_SUMMARY_HEADERS = ['Section', 'Field', 'Value']

const SUMMARY_SECTIONS = [
  ['TranDtls', 'TranDtls'],
  ['DocDtls', 'DocDtls'],
  ['SellerDtls', 'SellerDtls'],
  ['BuyerDtls', 'BuyerDtls'],
  ['ValDtls', 'ValDtls'],
]

function cellValue(value) {
  if (value == null) return ''
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'boolean') return value ? 'Y' : 'N'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function flattenSection(section, obj, rows) {
  if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) {
    rows.push({ Section: section, Field: '', Value: cellValue(obj) })
    return
  }
  Object.keys(obj).forEach((key) => {
    const v = obj[key]
    if (v != null && typeof v === 'object' && !Array.isArray(v)) {
      flattenSection(`${section}.${key}`, v, rows)
      return
    }
    rows.push({ Section: section, Field: key, Value: cellValue(v) })
  })
}

/**
 * Summary rows from a Version 1.1 e-invoice document.
 * @param {object} doc - output of buildEInvoiceDocument
 * @returns {{ Section: string, Field: string, Value: * }[]}
 */
export function buildEInvoiceSummaryRows(doc = {}) {
  const rows = [
    { Section: 'Header', Field: 'Version', Value: cellValue(doc.Version) },
  ]
  SUMMARY_SECTIONS.forEach(([key, label]) => {
    flattenSection(label, doc[key], rows)
  })
  return rows
}

/**
 * One spreadsheet row per ItemList entry (scalar columns only).
 * @param {object} doc
 * @returns {object[]}
 */
export function buildEInvoiceLineItemRows(doc = {}) {
  const items = Array.isArray(doc.ItemList) ? doc.ItemList : []
  return items.map((item) => {
    const row = {}
    Object.keys(item || {}).forEach((key) => {
      const v = item[key]
      if (v != null && typeof v === 'object') return
      row[key] = cellValue(v)
    })
    return row
  })
}

export function buildEInvoiceWorkbook(invoice = {}) {
  const doc = buildEInvoiceDocument(invoice)
  const workbook = XLSX.utils.book_new()
  const summaryRows = buildEInvoiceSummaryRows(doc)
  const summarySheet = XLSX.utils.json_to_sheet(summaryRows, { header: EINVOICE_SUMMARY_HEADERS })
  XLSX.utils.book_append_sheet(workbook, summarySheet, EINVOICE_SUMMARY_SHEET)

  const lineRows = buildEInvoiceLineItemRows(doc)
  const lineSheet = lineRows.length
    ? XLSX.utils.json_to_sheet(lineRows)
    : XLSX.utils.json_to_sheet([{ '(no line items)': '' }])
  XLSX.utils.book_append_sheet(workbook, lineSheet, EINVOICE_LINE_ITEMS_SHEET)
  return workbook
}

/**
 * Browser download. Filename stays `{invoice_id}-einvoice.xlsx` (JSON twin).
 * @param {object} invoice
 * @returns {string} filename
 */
export function downloadEInvoiceExcel(invoice = {}) {
  const filename = buildEInvoiceDownloadFilename(invoice, 'xlsx')
  const workbook = buildEInvoiceWorkbook(invoice)
  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
  const blob = new Blob([buffer], { type: XLSX_MIME })
  saveAs(blob, filename)
  return filename
}

/* ── Contractual upload helpers (not used by Overview → Download Excel) ── */

export const INVOICE_EXCEL_HEADERS = ['Candidate', 'Days', 'Rate', 'Amount']
export const INVOICE_EXCEL_SHEET_NAME = 'Items'

const CANDIDATE_KEYS = [
  'candidateName', 'candidate_name', 'Candidate', 'candidate',
  'CANDIDATE_NAME', 'employeeName', 'employee_name',
]

const DESCRIPTION_KEYS = [
  'itemName', 'item_name', 'title', 'description', 'PrdDesc',
  'particulars', 'name', 'positionName', 'descriptionOfGoods',
]

const DAYS_KEYS = [
  'presentDays', 'present_days', 'days', 'Days', 'Days Present',
  'totalBillableDays', 'billableDays', 'num_of_days', 'numberOfDays',
  'quantity', 'qty', 'Qty',
]

const RATE_KEYS = [
  'rate', 'Rate', 'unitPrice', 'unit_price', 'UnitPrice',
  'rateCard', 'rate_card', 'computed_rate_card',
]

const AMOUNT_KEYS = [
  'amount', 'Amount', 'baseAmount', 'base_amount',
  'taxableValue', 'lineTotal', 'line_total',
]

function pickFirst(obj, keys) {
  if (!obj || typeof obj !== 'object') return undefined
  for (let i = 0; i < keys.length; i += 1) {
    const v = obj[keys[i]]
    if (v != null && v !== '') return v
  }
  return undefined
}

function asCellNumber(value) {
  if (value == null || value === '') return ''
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const n = parseFloat(String(value).replace(/,/g, ''))
  return Number.isFinite(n) ? n : ''
}

function asCellText(value) {
  if (value == null) return ''
  return String(value).trim()
}

function fallbackSources(invoice = {}) {
  const list = invoice.candidateList || invoice.candidates
  if (Array.isArray(list) && list.length) return list
  return [invoice]
}

/** One Candidate / Days / Rate / Amount row — contractual billing upload only. */
export function mapInvoiceBillingRow(item = {}, invoice = {}) {
  const candidate = asCellText(
    pickFirst(item, CANDIDATE_KEYS)
    || pickFirst(item, DESCRIPTION_KEYS)
    || pickFirst(invoice, CANDIDATE_KEYS)
    || pickFirst(invoice, DESCRIPTION_KEYS),
  )
  return {
    Candidate: candidate,
    Days: asCellNumber(pickFirst(item, DAYS_KEYS)),
    Rate: asCellNumber(pickFirst(item, RATE_KEYS)),
    Amount: asCellNumber(pickFirst(item, AMOUNT_KEYS) ?? pickFirst(invoice, ['base_amount', 'baseAmount', 'amount'])),
  }
}

function isBlankBillingRow(row) {
  return !row.Candidate && row.Days === '' && row.Rate === '' && row.Amount === ''
}

export function buildInvoiceBillingRows(invoice = {}) {
  const lines = collectLineItems(invoice)
  const sources = lines.length ? lines : fallbackSources(invoice)
  const rows = sources.map((item) => mapInvoiceBillingRow(item, invoice))
  if (lines.length) return rows
  return rows.filter((row) => !isBlankBillingRow(row))
}

export function buildInvoiceExcelWorkbook(invoice = {}) {
  const rows = buildInvoiceBillingRows(invoice)
  const workbook = XLSX.utils.book_new()
  const sheet = XLSX.utils.json_to_sheet(rows, { header: INVOICE_EXCEL_HEADERS })
  XLSX.utils.book_append_sheet(workbook, sheet, INVOICE_EXCEL_SHEET_NAME)
  return workbook
}

export default {
  EINVOICE_SUMMARY_SHEET,
  EINVOICE_LINE_ITEMS_SHEET,
  EINVOICE_SUMMARY_HEADERS,
  buildEInvoiceSummaryRows,
  buildEInvoiceLineItemRows,
  buildEInvoiceWorkbook,
  downloadEInvoiceExcel,
  INVOICE_EXCEL_HEADERS,
  INVOICE_EXCEL_SHEET_NAME,
  mapInvoiceBillingRow,
  buildInvoiceBillingRows,
  buildInvoiceExcelWorkbook,
}
